import styled, {css} from 'styled-components'

interface DayCellProps {
    $isOutside: boolean
    $hightlightAvailabilities: boolean
    $isStart: boolean
    $isEnd: boolean
    $isMiddle: boolean
    $isWarning: boolean
}

// Define palette type for better type safety
type Palette = {
    primary: Record<string, string>
    neutral: Record<string, string>
    warning: Record<string, string>
}

// Helper function to determine border radius based on position
const getBorderRadius = (isStart: boolean, isEnd: boolean, isMiddle: boolean): string => {
    if (isStart && isEnd) return '6px'
    if (isStart) return '6px 0 0 6px'
    if (isEnd) return '0 6px 6px 0'
    if (isMiddle) return '0'
    return '6px'
}

// Helper function for base cell styles
const getBaseCellStyles = (palette: Palette, isOutside: boolean, hightlightAvailabilities: boolean) => {
    if (isOutside) {
        return css`
            cursor: default;
            color: ${palette.neutral['300']};
            background-color: transparent;
        `
    }

    return css`
        background-color: ${hightlightAvailabilities ? palette.primary['50'] : 'transparent'};
        color: ${palette.primary['500']};
    `
}

// Helper function for hover styles
const getHoverStyles = (palette: Palette, hightlightAvailabilities: boolean) => css`
    background-color: ${hightlightAvailabilities ? palette.primary['100'] : palette.neutral['100']};
`

// Helper function for selected state styles
const getSelectedStyles = (
    palette: Palette,
    isOutside: boolean,
    isStart: boolean,
    isEnd: boolean,
    isMiddle: boolean,
    isWarning: boolean
) => {
    const borderRadius = getBorderRadius(isStart, isEnd, isMiddle)

    if (isOutside) {
        if (isMiddle) {
            return css`
                background-color: ${palette.neutral['50']};
                color: ${palette.neutral['900']};
                border-radius: ${borderRadius};
            `
        }

        return css`
            color: ${palette.neutral['white']};
            background-color: ${palette.primary['200']};
            border-radius: ${borderRadius};
        `
    }

    if (isMiddle) {
        return css`
            background-color: ${palette.neutral['200']};
            color: ${palette.neutral['900']};
            border-radius: ${borderRadius};
        `
    }

    if (isWarning) {
        return css`
            background-color: ${palette.warning['600']};
            color: ${palette.neutral['white']};
            border-radius: ${borderRadius};
            &:hover {
                background-color: ${palette.warning['700']};
            }
        `
    }

    return css`
        background-color: ${palette.primary['500']};
        color: ${palette.neutral['white']};
        border-radius: ${borderRadius};

        .today-indicator {
            background-color: ${palette.neutral['white']};
        }
    `
}

// Helper function for disabled state styles
const getDisabledStyles = (palette: Palette) => css`
    background-color: transparent;
    color: ${palette.neutral['300']};
    pointer-events: none;

    &:hover {
        background-color: transparent;
    }
`

export const StyledDayCell = styled.td<DayCellProps>(
    ({theme: {palette}, $isOutside, $hightlightAvailabilities, $isStart, $isEnd, $isMiddle, $isWarning}) => css`
        position: relative;
        border-radius: 6px;
        width: 36px;
        height: 36px;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;

        /* Base styles */
        ${getBaseCellStyles(palette, $isOutside, $hightlightAvailabilities)}

        /* Hover styles */
        ${!$isOutside &&
        css`
            &:hover {
                ${getHoverStyles(palette, $hightlightAvailabilities)}
            }
        `}

        /* Selected state styles */
        &[data-selected='true'] {
            ${getSelectedStyles(palette, $isOutside, $isStart, $isEnd, $isMiddle, $isWarning)}
        }

        /* Disabled state styles */
        &[data-disabled='true'] {
            ${getDisabledStyles(palette)}
        }

        ${$isWarning &&
        css`
            color: ${palette.warning['700']};
            background-color: ${palette.warning['50']};
            &:hover {
                background-color: ${palette.warning['100']};
            }
        `}
    `
)

export const StyledTodayIndicator = styled.span(
    ({theme: {palette}}) => css`
        background-color: ${palette.primary['500']};
        border-radius: 50%;
        width: 3px;
        height: 3px;
        bottom: 3px;
        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%);
    `
)
